import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { CheckCircle2, Loader2 } from 'lucide-react';

/**
 * Public "forgot password" page. Always shows the neutral success
 * message after submit — the backend deliberately doesn't reveal
 * whether the email is registered.
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch {
      // Even on error, show the neutral message so we don't leak.
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#FAF9F6' }}>
      <Card className="w-full max-w-md p-8 shadow-lg" data-testid="forgot-password-page">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#2D4A3E' }}>
            Forgot your password?
          </h1>
          <p className="text-muted-foreground text-sm">
            Enter your email address and we&apos;ll send you a link to reset your password.
          </p>
        </div>

        {sent ? (
          <div className="text-center space-y-4">
            <CheckCircle2 className="h-12 w-12 mx-auto text-green-600" />
            <p className="text-sm text-stone-700">
              If an account exists for that email, we&apos;ve sent a reset link.
              Please check your inbox — the link is valid for one hour.
            </p>
            <Link to="/login" className="inline-block font-medium" style={{ color: '#2D4A3E' }} data-testid="forgot-back-to-signin">
              Back to sign-in
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-email-input"
                required
                className="input-custom mt-1"
              />
            </div>
            <Button
              type="submit"
              className="w-full btn-primary"
              disabled={loading}
              data-testid="forgot-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…
                </>
              ) : (
                'Send reset link'
              )}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              Remembered it?{' '}
              <Link to="/login" className="font-medium" style={{ color: '#2D4A3E' }}>
                Back to sign-in
              </Link>
            </p>
          </form>
        )}
      </Card>
    </div>
  );
}
